import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ArrowLeft, FileText, Search, CalendarCheck, ArrowUpDown, Trash2 } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mdy = (s) => { if (!s) return ""; const p = String(s).split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : s; };

export default function AdminClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("latest");
  const [onlySoon, setOnlySoon] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleOne = (email) => setSelected((s) => { const n = new Set(s); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const toggleAll = (emails, on) => setSelected((s) => { const n = new Set(s); emails.forEach((e) => (on ? n.add(e) : n.delete(e))); return n; });

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const emails = Array.from(selected);
      const { data } = await api.post("/admin/clients/bulk-delete", { emails });
      setClients((list) => list.filter((c) => !selected.has(c.client_email)));
      setSelected(new Set());
      setBulkOpen(false);
      toast.success(`Deleted ${data.clients} client${data.clients === 1 ? "" : "s"} · ${data.quotes_deleted} quote${data.quotes_deleted === 1 ? "" : "s"} removed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    api.get("/admin/clients")
      .then(({ data }) => setClients(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const soonCount = clients.filter((c) => c.delivery_soon).length;
  const term = q.trim().toLowerCase();
  const searched = term
    ? clients.filter((c) =>
        (c.client_name || "").toLowerCase().includes(term) ||
        (c.client_email || "").toLowerCase().includes(term) ||
        (c.company || "").toLowerCase().includes(term))
    : clients;
  const filtered = onlySoon ? searched.filter((c) => c.delivery_soon) : searched;
  const visible = sortBy === "completion"
    ? [...filtered].sort((a, b) => {
        const da = a.expected_completion || "9999-12-31";
        const db_ = b.expected_completion || "9999-12-31";
        return da < db_ ? -1 : da > db_ ? 1 : 0;
      })
    : filtered;

  const openClient = (c) => navigate(`/admin/clients/${encodeURIComponent(c.client_email)}`);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-clients">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate("/admin")} className="font-mono text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1 hover:underline" data-testid="back-to-overview">
            <ArrowLeft className="h-3 w-3" /> Admin Console
          </button>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Clients</h1>
          <p className="mt-1 font-mono text-xs text-zinc-500">{clients.length} {clients.length === 1 ? "client" : "clients"} across all quotes &amp; leads</p>
        </div>
        <Button onClick={() => navigate("/admin/quotes")} data-testid="clients-to-quotes-btn" variant="outline" className="rounded-none border-zinc-900 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 font-mono uppercase tracking-widest text-xs">
          <FileText className="h-4 w-4 mr-1" /> Quotes
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="client-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or company"
            className="pl-9 rounded-none border-zinc-300 focus-visible:ring-primary"
          />
        </div>
        <button
          data-testid="sort-toggle"
          onClick={() => setSortBy((s) => (s === "completion" ? "latest" : "completion"))}
          className={`flex items-center gap-1.5 px-3 py-2 font-mono text-[11px] uppercase tracking-widest border transition-colors ${sortBy === "completion" ? "border-primary text-primary bg-primary/5" : "border-zinc-300 text-zinc-500 hover:border-zinc-500"}`}
          title="Toggle sort"
        >
          <ArrowUpDown className="h-3.5 w-3.5" /> Sort: {sortBy === "completion" ? "Completion date" : "Latest activity"}
        </button>
        <button
          data-testid="delivering-soon-toggle"
          onClick={() => setOnlySoon((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 font-mono text-[11px] uppercase tracking-widest border transition-colors ${onlySoon ? "border-amber-500 text-amber-700 bg-amber-50" : "border-zinc-300 text-zinc-500 hover:border-zinc-500"}`}
          title="Show only clients delivering within 7 days"
        >
          <CalendarCheck className="h-3.5 w-3.5" /> Delivering this week{soonCount ? ` (${soonCount})` : ""}
        </button>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500">Loading…</p>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          {selected.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5 border-b border-primary/20" data-testid="clients-bulk-bar">
              <span className="font-mono text-xs uppercase tracking-widest text-primary">{selected.size} selected</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="rounded-none font-mono text-xs uppercase tracking-widest">Clear</Button>
                <Button size="sm" data-testid="clients-bulk-delete-btn" onClick={() => setBulkOpen(true)} className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono text-xs uppercase tracking-widest">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    data-testid="clients-select-all"
                    checked={visible.length > 0 && visible.every((c) => selected.has(c.client_email))}
                    onCheckedChange={(v) => toggleAll(visible.map((c) => c.client_email), !!v)}
                  />
                </th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-right px-4 py-3">Quotes</th>
                <th className="text-right px-4 py-3">Total Quoted</th>
                <th className="text-right px-4 py-3">Total Paid</th>
                <th className="text-left px-4 py-3">Completion</th>
                <th className="text-left px-4 py-3">Latest</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr
                  key={c.client_email}
                  data-testid={`client-row-${c.client_email}`}
                  onClick={() => openClient(c)}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox data-testid={`client-select-${c.client_email}`} checked={selected.has(c.client_email)} onCheckedChange={() => toggleOne(c.client_email)} />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium flex items-center gap-2">
                      {c.client_name || "—"}
                      {c.is_lead && <span className="font-mono text-[9px] uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5">Lead</span>}
                      {c.has_account && <span className="font-mono text-[9px] uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5">Account</span>}
                    </p>
                    <p className="text-xs text-zinc-500">{c.client_email}</p>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{c.company || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.quote_count}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(c.total_quoted)}</td>
                  <td className="px-4 py-3 text-right font-mono text-green-700">{fmt(c.total_paid)}</td>
                  <td className="px-4 py-3" data-testid={`client-completion-${c.client_email}`}>
                    {c.expected_completion ? (
                      <span className={`inline-flex items-center gap-1 font-mono text-[11px] ${c.delivery_overdue ? "text-red-600" : c.delivery_soon ? "text-amber-700 font-semibold" : "text-zinc-600"}`}>
                        <CalendarCheck className="h-3.5 w-3.5" />
                        {mdy(c.expected_completion)}
                        {c.delivery_soon && <span className="ml-1 font-mono text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5">This week</span>}
                        {c.delivery_overdue && <span className="ml-1 font-mono text-[9px] uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5">Overdue</span>}
                      </span>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.latest_status} />
                    <p className="text-xs text-zinc-400 font-mono mt-1">{c.latest_date ? new Date(c.latest_date).toLocaleDateString() : ""}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visible.length === 0 && <p className="p-10 text-center text-zinc-500">{onlySoon ? "No clients delivering in the next 7 days." : term ? "No clients match your search." : "No clients yet."}</p>}
        </div>
      )}

      <AlertDialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} client{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              For each selected client this permanently removes ALL their quotes (including signed/paid), notes, onboarding records, payment records, and their login account. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-clients-bulk-delete-btn" disabled={bulkDeleting} onClick={(e) => { e.preventDefault(); bulkDelete(); }} className="rounded-none bg-red-600 hover:bg-red-700">
              {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
